import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Avatar,
  Chip,
  Button,
} from "@mui/material";
import {
  AccountCircle,
  Phone,
  Language,
  Public,
  EmojiObjects,
  Interests,
  School,
  CalendarMonth,
  Business,
  Cake,
  Code,
  LinkedIn,
  Edit,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { getCurrentLearner, resetAllActions } from "../../State/Learner/Action";
import { useNavigate } from "react-router-dom";

const IconLabel = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      {icon}
      <Typography variant="subtitle1" fontWeight="bold">
        {label}:
      </Typography>
    </Box>
    <Typography variant="body1" sx={{ ml: 4 }}>
      {value || "N/A"}
    </Typography>
  </Grid>
);

const LearnerDetail = () => {
  const dispatch = useDispatch();
  const learner = useSelector(
    (state) => state.learner?.currentLearner?.data || {}
  );

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getCurrentLearner());
    dispatch(resetAllActions());
  }, [dispatch]);

  return (
    <Box
      sx={{
        background: "linear-gradient(to right, #e0f7fa, #fce4ec)",
        minHeight: "100vh",
        py: 6,
        px: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            maxWidth: 1000,
            mx: "auto",
            borderRadius: 4,
            backgroundColor: "#ffffffdd",
          }}
        >
          <Typography variant="h4" align="center" gutterBottom>
            🎓 Learner Profile
          </Typography>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Edit />}
                onClick={() => navigate("/profile-edit")} // or open a dialog/modal
              >
                Edit Profile
              </Button>
            </motion.div>
          </Box>

          <Box textAlign="center" mb={4}>
            <Avatar
              src={learner.profilePictureUrl}
              alt="Profile"
              sx={{ width: 120, height: 120, mx: "auto", mb: 2 }}
            />
            <Typography variant="h6" fontWeight="bold">
              {learner.fullName}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              @{learner.username}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <IconLabel
              icon={<Phone color="primary" />}
              label="Phone"
              value={learner.phone}
            />
            <IconLabel
              icon={<Public color="primary" />}
              label="Country"
              value={learner.country}
            />
            <IconLabel
              icon={<Language color="primary" />}
              label="Preferred Language"
              value={learner.preferredLanguage}
            />
            <IconLabel
              icon={<EmojiObjects color="primary" />}
              label="Learning Goal"
              value={learner.learningGoal}
            />
            <IconLabel
              icon={<School color="primary" />}
              label="Qualification"
              value={learner.highestQualification}
            />
            <IconLabel
              icon={<CalendarMonth color="primary" />}
              label="Year of Qualification"
              value={learner.yearOfQualification}
            />
            <IconLabel
              icon={<Business color="primary" />}
              label="Industry Experience"
              value={learner.industryExperience}
            />
            <IconLabel
              icon={<Cake color="primary" />}
              label="Date of Birth"
              value={learner.dateOfBirth}
            />
            <IconLabel
              icon={<Code color="primary" />}
              label="GitHub"
              value={learner.githubLink}
            />
            <IconLabel
              icon={<LinkedIn color="primary" />}
              label="LinkedIn"
              value={learner.linkedInUrl}
            />

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Interests color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Interested Categories:
                </Typography>
              </Box>
              <Box sx={{ ml: 4, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {learner.interestedCategories?.map((category, index) => (
                  <Chip key={index} label={category} color="secondary" />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default LearnerDetail;
